import React, { useState, useEffect, useRef } from 'react'
import './Verification.css';
import { useNavigate } from "react-router-dom";
import { sendTextMessage } from '../../../services/sendTextMessage';
import { registerUser } from '../../../services/registerUser';
import { useLocation } from 'react-router-dom';
import RingLoader from 'react-spinners/RingLoader';
import  secureLocalStorage  from  "react-secure-storage";
import { userLogin } from '../../../services/userLogin';
import { useDispatch } from 'react-redux';
import { save } from '../../../features/token/tokenSaver';
import { persistUserSession } from '../../../services/userSession';
import { getUserData } from '../../../services/getUserData';
import {
    getUserDashboardRouteState,
    isUserDashboardEligible,
    normalizeUserPhone,
} from '../../../utils/userRouting';
import { isAmazonTestPhone, AMAZON_TEST_PHONE, AMAZON_TEST_CODE } from '../../../utils/amazonTestPhone';

export default function Verification() {
    const [veriCode, setVeriCode] = useState("");
    const [resendDisabled, setResendDisabled] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("No matching phone number and code found");
    const [timeLeftValue, setTimeLeftValue] = useState(60);
    const { state } = useLocation();
    const [loading, setLoading] = useState(false);
    const verificationInputRef = useRef(null);
    const intervalRef = useRef(null);
    const dispatch = useDispatch()

    const navigate = useNavigate();

    function stopCounter() {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }

    function startCounter() {
        stopCounter();
        intervalRef.current = setInterval(counter, 1000);
    }

    // Ask the server to send a fresh code. The code itself is generated and
    // validated server-side; the client never sees or chooses it.
    function requestCode(phoneNumber) {
        secureLocalStorage.setItem("ls", new Date());
        setResendDisabled(true);
        startCounter();
        return sendTextMessage(phoneNumber).catch((err) => {
            // Surface server rate-limit (resend cooldown) without leaking detail.
            console.log(err);
        });
    }

    useEffect(() => {
        stopCounter();

        if (!state) {
            navigate("/");
        }
        else if (isAmazonTestPhone(state.user.phoneNumber)) {
            setLoading(true);
            userLogin({ phoneNumber: state.user.phoneNumber, code: AMAZON_TEST_CODE }).then((result) => {
                dispatch(save(result.data.token));
                persistUserSession({
                    token: result.data.token,
                    refreshToken: result.data.refreshToken,
                    phoneNumber: AMAZON_TEST_PHONE,
                    userId: result.data.userId,
                });
                navigate('/user/linkNewScreen/', {
                    state: { phoneNumber: AMAZON_TEST_PHONE, token: result.data.token },
                });
            }).catch(() => {
                setError(true);
                setErrorMessage('Amazon test login is not available on this server.');
                setLoading(false);
            });
        }
        else if (state.user.phoneNumber) {
            secureLocalStorage.setItem("phoneNumber", state.user.phoneNumber.replace('+1', ''));
            setLoading(true);

            const ls = secureLocalStorage.getItem("ls");
            const now = new Date();
            // Only auto-send if we have not recently sent (avoids double-send on
            // re-mount and respects the server-side resend cooldown).
            if (ls && (now - new Date(ls)) < 60000) {
                setResendDisabled(true);
                startCounter();
                setLoading(false);
            } else {
                requestCode(state.user.phoneNumber).finally(() => setLoading(false));
            }
        }
        else {
            navigate("/");
        }

        if (verificationInputRef.current) {
            verificationInputRef.current.focus();
        }

        return () => {
            stopCounter();
        };
    }, []);

    function counter() {
        var now = new Date();
        var ls = secureLocalStorage.getItem("ls");
        var timeLeft = (60 - Math.ceil((now - new Date(ls)) / 1000));
        setTimeLeftValue(timeLeft);
        if (timeLeft <= 0) {
            setResendDisabled(false);
            stopCounter();
        }
    }

    function onChange(event) {
        setVeriCode(event.target.value);
    }

    function resendCode() {
        if (!resendDisabled) {
            requestCode(state.user.phoneNumber);
        }
    }

    function onSubmit(event) {
        event.preventDefault();
        setLoading(true);
        setError(false);

        if (state.toRegister) {
            // Server creates the account only after validating the OTP it issued.
            registerUser({ ...state.user, code: veriCode })
                .then(function (result) {
                    dispatch(save(result.data.token));
                    persistUserSession({
                        token: result.data.token,
                        refreshToken: result.data.refreshToken,
                        phoneNumber: secureLocalStorage.getItem("phoneNumber")
                    });
                    navigate("/user/todo", { state: { phoneNumber: secureLocalStorage.getItem("phoneNumber"), token: result.data.token } })
                })
                .catch(function (err) {
                    setLoading(false);
                    setError(true);
                    setErrorMessage(err?.response?.status === 401
                        ? 'That code is incorrect or expired. Request a new code.'
                        : 'Something went wrong. Please try again.');
                });
        }
        else {
            userLogin({ phoneNumber: state.user.phoneNumber, code: veriCode }).then(async (result) => {
                dispatch(save(result.data.token));
                const phone = normalizeUserPhone(state.user.phoneNumber);
                const token = result.data.token;
                persistUserSession({
                    token,
                    refreshToken: result.data.refreshToken,
                    phoneNumber: phone,
                    userId: result.data.userId
                });

                try {
                    const profileResponse = await getUserData(phone, token);
                    if (isUserDashboardEligible(profileResponse.data)) {
                        navigate("/user/compose", {
                            replace: true,
                            state: getUserDashboardRouteState(profileResponse.data, phone, token),
                        });
                        return;
                    }
                } catch {
                    // Fall back to onboarding todo when profile lookup fails.
                }

                navigate("/user/todo", {
                    replace: true,
                    state: {
                        phoneNumber: phone,
                        token,
                        userId: result.data.userId
                    }
                });
            }).catch((err) => {
                setLoading(false);
                setError(true);
                setErrorMessage(err?.response?.status === 400 || err?.response?.status === 401
                    ? 'That code is incorrect or expired. Request a new code.'
                    : 'Something went wrong. Please try again.');
            })
        }
    }
    return (
        <div className='verificationFlowPage'>
            <div className='verificationFlowContainer'>
                <section className='verificationFlowInfoPanel'>
                    <span className='verificationFlowEyebrow'>Secure Sign-In</span>
                    <h1 className='verificationFlowHeadline'>Verify Your Number</h1>
                    <p className='verificationFlowDescription'>
                        We sent a 6-digit verification code to your phone. Enter it below to continue to your account.
                    </p>
                    <div className='verificationFlowTips'>
                        <div className='verificationFlowTip'>
                            <span className='verificationFlowTipNumber'>1</span>
                            <span className='verificationFlowTipText'>Use the latest code from your text messages.</span>
                        </div>
                        <div className='verificationFlowTip'>
                            <span className='verificationFlowTipNumber'>2</span>
                            <span className='verificationFlowTipText'>The code expires quickly for account security.</span>
                        </div>
                        <div className='verificationFlowTip'>
                            <span className='verificationFlowTipNumber'>3</span>
                            <span className='verificationFlowTipText'>Request a new code if the timer reaches zero.</span>
                        </div>
                    </div>
                </section>

                <section className='verificationFlowFormPanel'>
                    <form className='verificationFlowForm' onSubmit={onSubmit}>
                        <span className='verificationFlowBrand'>INVERT</span>
                        <h2 className='verificationFlowTitle'>Enter Verification Code</h2>
                        <p className='verificationFlowSubTitle'>Type the 6-digit code we sent via text.</p>

                        <label className='verificationFlowInputLabel' htmlFor='verificationInput'>6-Digit Code</label>
                        <input
                            id='verificationInput'
                            ref={verificationInputRef}
                            value={veriCode}
                            onChange={onChange}
                            required
                            className='verificationInput'
                            type='text'
                            pattern='\d*'
                            maxLength='6'
                            minLength={6}
                            placeholder='- - - - - -'
                        />

                        {error && <span className='errorPhoneVerification'>{errorMessage}</span>}

                        <button className='verificationFlowSubmitButton' type='submit' value={'Save'} disabled={loading}>
                            {!loading && 'Verify & Continue'}
                            {loading &&
                                <span className='verificationRingLoaderContainer'>
                                    <RingLoader
                                        color={'#ffffff'}
                                        loading={true}
                                        size={20}
                                        aria-label='Loading Spinner'
                                        data-testid='loader'
                                    />
                                </span>
                            }
                        </button>

                        <button
                            type='button'
                            onClick={() => { resendCode() }}
                            className={`resendButton ${resendDisabled ? 'disabledResendLink' : ''}`}
                            disabled={resendDisabled}
                        >
                            Resend Code {resendDisabled && timeLeftValue > 0 && `(${timeLeftValue}sec)`}
                        </button>
                    </form>
                </section>
            </div>
        </div>
    )
}
